/**
 * VirtualStageManager - Admin component for managing virtual stages
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Video, Radio, Play, Square, 
  Users, ExternalLink, Loader2, Settings, Eye, Hand, Mic
} from 'lucide-react';
import { virtualStageAPI } from '@/lib/api';
import SpeakerTranscriptionPanel from './SpeakerTranscriptionPanel';

const STREAM_PLATFORMS = [
  { value: 'custom', label: 'Custom Embed/URL' },
  { value: 'youtube', label: 'YouTube Live' },
  { value: 'vimeo', label: 'Vimeo' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'rtmp', label: 'RTMP Stream' },
  { value: 'hls', label: 'HLS Stream' },
  { value: 'webrtc', label: 'WebRTC' },
  { value: 'zoom', label: 'Zoom Webinar' },
  { value: 'teams', label: 'Microsoft Teams' },
  { value: 'facebook', label: 'Facebook Live' },
  { value: 'iframe', label: 'Embed (iframe)' },
];

const StageForm = ({ stage, onSubmit, onCancel }) => {
  const [form, setForm] = useState(stage || {
    title: '',
    description: '',
    stream_type: 'custom',
    stream_url: '',
    stream_key: '',
    chat_enabled: true,
    qa_enabled: true,
    interpreter_type: 'none',
    interpreter_stream_url: '',
    scheduled_start: '',
    scheduled_end: '',
  });

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Stage Title *</Label>
          <Input 
            value={form.title} 
            onChange={e => setForm({...form, title: e.target.value})}
            placeholder="Main Stage"
          />
        </div>
        <div>
          <Label>Platform</Label>
          <Select value={form.stream_type} onValueChange={v => setForm({...form, stream_type: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {STREAM_PLATFORMS.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <Textarea 
          value={form.description || ''} 
          onChange={e => setForm({...form, description: e.target.value})}
          rows={2}
          placeholder="Stage description..."
        />
      </div>

      <div>
        <Label>Stream URL / Video ID</Label>
        <Input 
          value={form.stream_url || ''} 
          onChange={e => setForm({...form, stream_url: e.target.value})}
          placeholder="https://youtube.com/watch?v=... or video ID"
        />
      </div>

      {['custom', 'rtmp', 'hls', 'webrtc'].includes(form.stream_type) && (
        <div>
          <Label>Stream Key / Access Token</Label>
          <Input 
            value={form.stream_key || ''} 
            onChange={e => setForm({...form, stream_key: e.target.value})}
            placeholder="Your stream key or access token (optional)"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Scheduled Start</Label>
          <Input 
            type="datetime-local"
            value={form.scheduled_start || ''} 
            onChange={e => setForm({...form, scheduled_start: e.target.value})}
          />
        </div>
        <div>
          <Label>Scheduled End</Label>
          <Input 
            type="datetime-local"
            value={form.scheduled_end || ''} 
            onChange={e => setForm({...form, scheduled_end: e.target.value})}
          />
        </div>
      </div>

      <div className="space-y-3 pt-2">
        <div className="flex items-center justify-between">
          <Label>Enable Live Chat</Label>
          <Switch 
            checked={form.chat_enabled} 
            onCheckedChange={v => setForm({...form, chat_enabled: v})}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label>Enable Q&A</Label>
          <Switch 
            checked={form.qa_enabled} 
            onCheckedChange={v => setForm({...form, qa_enabled: v})}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label>Sign Language Interpreter</Label>
          <Switch 
            checked={form.interpreter_type !== 'none'} 
            onCheckedChange={v => setForm({...form, interpreter_type: v ? 'live' : 'none'})}
          />
        </div>
      </div>

      {form.interpreter_type !== 'none' && (
        <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Interpreter Type</Label>
              <Select value={form.interpreter_type} onValueChange={v => setForm({...form, interpreter_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="live">Live Person</SelectItem>
                  <SelectItem value="ai_avatar">AI Avatar (Professional)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sign Language</Label>
              <Select value={form.interpreter_language || 'asl'} onValueChange={v => setForm({...form, interpreter_language: v})}>
                <SelectTrigger><SelectValue placeholder="Select language" /></SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">🌍 Africa (8)</div>
                  <SelectItem value="sasl">🇿🇦 SASL - South African</SelectItem>
                  <SelectItem value="zsl">🇿🇼 ZSL - Zimbabwe</SelectItem>
                  <SelectItem value="ksl">🇰🇪 KSL - Kenyan</SelectItem>
                  <SelectItem value="ugsl">🇺🇬 UGSL - Ugandan</SelectItem>
                  <SelectItem value="tsl">🇹🇿 TSL - Tanzanian</SelectItem>
                  <SelectItem value="gsl">🇬🇭 GSL - Ghanaian</SelectItem>
                  <SelectItem value="nsl">🇳🇬 NSL - Nigerian</SelectItem>
                  <SelectItem value="esl">🇪🇹 ESL - Ethiopian</SelectItem>
                  <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">🌎 Americas (4)</div>
                  <SelectItem value="asl">🇺🇸 ASL - American</SelectItem>
                  <SelectItem value="lsm">🇲🇽 LSM - Mexican</SelectItem>
                  <SelectItem value="libras">🇧🇷 Libras - Brazilian</SelectItem>
                  <SelectItem value="lsa">🇦🇷 LSA - Argentine</SelectItem>
                  <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">🌍 Europe (7)</div>
                  <SelectItem value="bsl">🇬🇧 BSL - British</SelectItem>
                  <SelectItem value="lsf">🇫🇷 LSF - French</SelectItem>
                  <SelectItem value="dgs">🇩🇪 DGS - German</SelectItem>
                  <SelectItem value="lis">🇮🇹 LIS - Italian</SelectItem>
                  <SelectItem value="lse">🇪🇸 LSE - Spanish</SelectItem>
                  <SelectItem value="pjm">🇵🇱 PJM - Polish</SelectItem>
                  <SelectItem value="rsl">🇷🇺 RSL - Russian</SelectItem>
                  <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">🌏 Asia-Pacific (6)</div>
                  <SelectItem value="auslan">🇦🇺 Auslan - Australian</SelectItem>
                  <SelectItem value="nzsl">🇳🇿 NZSL - New Zealand</SelectItem>
                  <SelectItem value="jsl">🇯🇵 JSL - Japanese</SelectItem>
                  <SelectItem value="csl">🇨🇳 CSL - Chinese</SelectItem>
                  <SelectItem value="isl_india">🇮🇳 ISL - Indian</SelectItem>
                  <SelectItem value="hksl">🇭🇰 HKSL - Hong Kong</SelectItem>
                  <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">🌐 International</div>
                  <SelectItem value="is">🌍 IS - International Sign</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.interpreter_type === 'live' && (
            <div>
              <Label>Interpreter Stream URL</Label>
              <Input 
                value={form.interpreter_stream_url || ''} 
                onChange={e => setForm({...form, interpreter_stream_url: e.target.value})}
                placeholder="URL for interpreter video feed"
              />
            </div>
          )}
          {form.interpreter_type === 'ai_avatar' && (
            <div className="p-2 bg-blue-50 rounded text-xs text-blue-700 flex items-center gap-2">
              <span className="text-lg">🤖</span>
              <span>AI Avatar will provide real-time sign language interpretation using speech recognition with customizable appearance (skin tone, hair, clothing).</span>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-white">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(form)} disabled={!form.title}>
          {stage ? 'Update' : 'Create'} Stage
        </Button>
      </div>
    </div>
  );
};

const VirtualStageManager = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);

  const { data: stages = [], isLoading } = useQuery({
    queryKey: ['virtual-stages', eventId],
    queryFn: () => virtualStageAPI.adminList(eventId).then(r => Array.isArray(r.data) ? r.data : []).catch(() => []),
  });

  const createMutation = useMutation({
    mutationFn: (data) => virtualStageAPI.create(eventId, data),
    onSuccess: () => {
      toast.success('Stage created');
      queryClient.invalidateQueries(['virtual-stages', eventId]);
      setIsOpen(false);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create stage'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => virtualStageAPI.update(eventId, id, data),
    onSuccess: () => {
      toast.success('Stage updated');
      queryClient.invalidateQueries(['virtual-stages', eventId]);
      setEditingStage(null);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update stage'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => virtualStageAPI.delete(eventId, id),
    onSuccess: () => {
      toast.success('Stage deleted');
      queryClient.invalidateQueries(['virtual-stages', eventId]);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete stage'),
  });

  const goLiveMutation = useMutation({
    mutationFn: (stageId) => virtualStageAPI.goLive(eventId, stageId),
    onSuccess: () => {
      toast.success('Stage is now live!');
      queryClient.invalidateQueries(['virtual-stages', eventId]);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to go live'),
  });

  const endStreamMutation = useMutation({
    mutationFn: (stageId) => virtualStageAPI.endStream(eventId, stageId),
    onSuccess: () => {
      toast.success('Stream ended');
      queryClient.invalidateQueries(['virtual-stages', eventId]);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to end stream'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="virtual-stage-manager">
      <Tabs defaultValue="stages" className="w-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Video className="h-6 w-6" />
              Virtual Stages
            </h2>
            <Badge variant="outline">{stages.length} stages</Badge>
          </div>
          <TabsList>
            <TabsTrigger value="stages" className="gap-2">
              <Video className="h-4 w-4" />
              Stages
            </TabsTrigger>
            <TabsTrigger value="transcription" className="gap-2">
              <Hand className="h-4 w-4" />
              Live Transcription
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="stages" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="h-4 w-4 mr-2" />Add Stage</Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>Create Virtual Stage</DialogTitle>
                </DialogHeader>
                <StageForm 
                  onSubmit={data => createMutation.mutate(data)} 
                  onCancel={() => setIsOpen(false)} 
                />
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stages.map(stage => (
          <Card key={stage.id} data-testid={`stage-${stage.id}`} className={stage.status === 'live' ? 'border-red-500 border-2' : ''}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {stage.title}
                    {stage.status === 'live' && (
                      <Badge variant="destructive" className="animate-pulse">
                        <Radio className="h-3 w-3 mr-1" /> LIVE
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {STREAM_PLATFORMS.find(p => p.value === stage.stream_type)?.label || stage.stream_type}
                  </CardDescription>
                </div>
                <div className="flex gap-1">
                  <Dialog open={editingStage?.id === stage.id} onOpenChange={(open) => !open && setEditingStage(null)}>
                    <DialogTrigger asChild>
                      <Button size="icon" variant="ghost" onClick={() => setEditingStage(stage)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl">
                      <DialogHeader>
                        <DialogTitle>Edit Stage</DialogTitle>
                      </DialogHeader>
                      <StageForm 
                        stage={editingStage} 
                        onSubmit={data => updateMutation.mutate({ id: stage.id, data })} 
                        onCancel={() => setEditingStage(null)} 
                      />
                    </DialogContent>
                  </Dialog>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    className="text-red-600" 
                    onClick={() => deleteMutation.mutate(stage.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {stage.description && (
                <p className="text-sm text-muted-foreground line-clamp-2">{stage.description}</p>
              )}
              
              <div className="flex flex-wrap gap-2">
                {stage.chat_enabled && <Badge variant="secondary">Chat</Badge>}
                {stage.qa_enabled && <Badge variant="secondary">Q&A</Badge>}
                {stage.interpreter_type && stage.interpreter_type !== 'none' && (
                  <Badge variant="secondary">
                    {stage.interpreter_type === 'ai_avatar' ? 'AI Interpreter' : 'Live Interpreter'}
                  </Badge>
                )}
              </div>

              {stage.viewer_count > 0 && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  {stage.viewer_count} viewers
                </div>
              )}

              <div className="flex gap-2 pt-2">
                {stage.status === 'live' ? (
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => endStreamMutation.mutate(stage.id)}
                  >
                    <Square className="h-4 w-4 mr-1" /> End Stream
                  </Button>
                ) : (
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    onClick={() => goLiveMutation.mutate(stage.id)}
                  >
                    <Play className="h-4 w-4 mr-1" /> Go Live
                  </Button>
                )}
                <Button variant="outline" size="sm" asChild>
                  <a href={`/event/${eventId}/stage/${stage.id}`} target="_blank" rel="noopener noreferrer">
                    <Eye className="h-4 w-4 mr-1" /> Preview
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {stages.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          <Video className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No virtual stages yet. Create your first stage to enable live streaming!</p>
        </Card>
      )}
        </TabsContent>

        <TabsContent value="transcription">
          <SpeakerTranscriptionPanel eventId={eventId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VirtualStageManager;
